import { parseISO } from 'date-fns';

import type { FlightLeg, ItineraryCardProps } from './ItineraryCard';
import type { Itinerary, Leg } from '../../selectors/dealHunterTypes';

const formatTime = (isoString?: string): string => {
  if (!isoString) return '--:--';
  try {
    const date = parseISO(isoString);
    if (Number.isNaN(date.getTime())) return '--:--';
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  } catch {
    return '--:--';
  }
};

const formatDuration = (minutes?: number): string => {
  if (minutes == null || minutes < 0) return '';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}m`;
  if (mins === 0) return `${hours}h`;
  return `${hours}h ${String(mins).padStart(2, '0')}`;
};

const formatStopsLabel = (stopCount: number): string => {
  if (stopCount === 0) return 'Direct';
  if (stopCount === 1) return '1 stop';
  return `${stopCount} stops`;
};

const mapLeg = (leg: Leg): FlightLeg => ({
  departureTime: formatTime(leg.departure),
  arrivalTime: formatTime(leg.arrival),
  departureStation: leg.origin?.displayCode ?? '',
  arrivalStation: leg.destination?.displayCode ?? '',
  duration: formatDuration(leg.durationMinutes),
  stopCount: leg.stopCount ?? 0,
  stopsLabel: formatStopsLabel(leg.stopCount ?? 0),
});

export const mapItineraryToCardProps = (
  itinerary: Itinerary,
): ItineraryCardProps | null => {
  const { legs } = itinerary;
  if (!legs || legs.length === 0) return null;

  const outboundLeg = legs[0];
  const inboundLeg = legs.length > 1 ? legs[1] : undefined;

  const carrier = outboundLeg.carriers?.marketingCarriers?.[0];
  const airlineName = carrier?.name ?? '';
  const airlineLogoUrl = carrier?.logoUri;

  const outbound = mapLeg(outboundLeg);
  const inbound = inboundLeg ? mapLeg(inboundLeg) : undefined;

  const ariaLabel = [
    airlineName,
    `${outbound.departureStation} to ${outbound.arrivalStation}`,
    `${outbound.departureTime} to ${outbound.arrivalTime}`,
    outbound.stopsLabel,
    ...(inbound
      ? [
          `return ${inbound.departureTime} to ${inbound.arrivalTime}`,
          inbound.stopsLabel,
        ]
      : []),
  ]
    .filter(Boolean)
    .join(', ');

  return {
    airlineName,
    airlineLogoUrl,
    outbound,
    inbound,
    ariaLabel,
    id: itinerary.id,
  };
};
